/**
 * NERO PRO · Sunucu istemcisi
 *
 * Tek yerden geçen basit bir fetch sarmalayıcı. Harici veri kütüphanesi yok:
 * bağımlılık ne kadar az olursa, sistem yıllar sonra da o kadar kolay derlenir.
 */

/* ---- SAYFA ANAHTARI ----
   Oturum iki parçalıdır: httpOnly çerez (tarayıcı taşır) + bu anahtar.
   Anahtar YALNIZCA bu değişkende, bellekte durur. localStorage'a,
   sessionStorage'a ya da çereze HİÇ yazılmaz; bu bilinçli bir tercihtir:
   sayfa yenilendiğinde veya yeni sekme açıldığında bellek sıfırlanır,
   anahtar kaybolur ve sunucu isteği reddeder — giriş ekranı çıkar. */
let sayfaAnahtari = '';

/** Oturum katmanı çağırır: girişte anahtarı yerleştirir, çıkışta boşaltır. */
export function sayfaAnahtariniAyarla(anahtar: string): void {
  sayfaAnahtari = anahtar;
  /* Giriş ve çıkış aynı kapıdan geçer. Saklanan okumalar burada atılır:
     başka bir kullanıcının verisi ekranda tek kare bile görünmemeli. */
  onbellegiTemizle();
}

/** Her isteğe eklenen oturum başlığı. Anahtar yoksa hiç gönderilmez. */
export function oturumBasligi(): Record<string, string> {
  return sayfaAnahtari ? { 'x-nero-sayfa': sayfaAnahtari } : {};
}

export class ApiHatasi extends Error {
  constructor(readonly durum: number, mesaj: string, readonly kod?: string) {
    super(mesaj);
    this.name = 'ApiHatasi';
  }
  get oturumGitti(): boolean { return this.durum === 401; }
  get yetkiYok(): boolean { return this.durum === 403; }
}

async function govde(yanit: Response): Promise<unknown> {
  const tur = yanit.headers.get('content-type') ?? '';
  if (!tur.includes('application/json')) return null;
  try { return await yanit.json(); } catch { return null; }
}

async function istek<T>(yol: string, secenek: RequestInit = {}): Promise<T> {
  let yanit: Response;
  try {
    yanit = await fetch(`/api${yol}`, {
      credentials: 'same-origin',
      ...secenek,
      headers: {
        /* İÇERİK TÜRÜ YALNIZCA GÖVDE VARSA.
           Eskiden gövdesiz isteklere de (DELETE, GET) `application/json`
           konuyordu. Fastify bunu "JSON diyorsun ama gövde boş" diye
           reddediyor ve kullanıcı silme penceresinde şu hatayı görüyordu:
           "Body cannot be empty when content-type is set to 'application/json'".
           FormData'da türü tarayıcı kendisi (sınır dizgisiyle) yazar. */
        ...(secenek.body != null && !(secenek.body instanceof FormData)
          ? { 'content-type': 'application/json' }
          : {}),
        ...oturumBasligi(),
        ...secenek.headers,
      },
    });
  } catch {
    throw new ApiHatasi(0, 'Sunucuya ulaşılamıyor. Ağ bağlantısını kontrol edin.', 'AG');
  }

  const veri = await govde(yanit);
  if (!yanit.ok) {
    const h = veri as { hata?: string; kod?: string } | null;
    const hata = new ApiHatasi(yanit.status, h?.hata ?? 'Beklenmeyen bir hata oluştu', h?.kod);
    /* Oturum düştüğünde her ekran "İşlem tamamlanamadı" gösteriyordu.
       Artık uygulamaya haber veriliyor; oturum katmanı giriş ekranına döner. */
    if (hata.oturumGitti && !yol.startsWith('/oturum')) {
      /* Oturum düştüyse elimizdeki anahtar da ölüdür; saklamanın anlamı yok. */
      sayfaAnahtari = '';
      oturumDustu();
    }
    throw hata;
  }
  return veri as T;
}

/* ---- Oturum düşmesi bildirimi ----
   api katmanı React'i tanımaz; oturum katmanı buraya bir dinleyici bırakır. */
type OturumDinleyici = () => void;
let oturumDinleyici: OturumDinleyici | null = null;

/** Oturum katmanı çağırır: 401 gelince ne yapılacağını belirler. */
export function oturumDusunceCalistir(f: OturumDinleyici | null): void {
  oturumDinleyici = f;
}

function oturumDustu(): void {
  oturumDinleyici?.();
}

/* ══════════════ OKUMA ÖNBELLEĞİ ══════════════
 *
 * ŞİKÂYET: "uygulama yavaş geliyor."
 * ÖLÇÜLDÜ (gerçek Chromium, boş veritabanı): her ekran geçişi 53–174 ms
 * sürüyor ve HER SEFERİNDE boş bir "yükleniyor" durumundan geçiyor —
 * çünkü `useVeri` hiçbir şey saklamıyordu. Aynı adres tek oturumda 6 kez
 * çekiliyordu (`/api/is-emirleri?acik=1`); ilk çağrı 320 ms.
 * Dükkânda veritabanı büyüdükçe ve makine yük altındayken bu bekleme
 * doğrudan "kasıyor" hissi veriyor.
 *
 * ÇÖZÜM İKİ PARÇA — ikisi de VERİYİ ESKİTMEDEN çalışır:
 *
 *   1. SON YANIT SAKLANIR. Ekrana geri dönüldüğünde eldeki değer ANINDA
 *      boyanır, ağ isteği yine de yapılır ve yanıt gelince ekran
 *      tazelenir. Yani veri hiçbir zaman ağdan daha eski kalmaz; yalnız
 *      boş ekran beklemesi kalkar.
 *   2. AYNI ANDA GİDEN AYNI İSTEK BİRLEŞTİRİLİR. İki bileşen aynı adresi
 *      isterse tek istek gider.
 *
 * YAZMA HER ŞEYİ DÜŞÜRÜR. Bir POST/PUT/DELETE sonrası hangi listelerin
 * etkilendiğini tahmin etmek kırılgandır (bir iş emri kaydı kasayı, stoku
 * ve özeti birden değiştirir). Bu yüzden herhangi bir yazma işleminden
 * sonra saklanan her şey atılır: ilk boyama tazelenene kadar ağdan gelir.
 *
 * OTURUM DEĞİŞİNCE DE TEMİZLENİR — başka kullanıcının verisi ekranda
 * bir kare bile görünmemeli.
 */
const okumaOnbellegi = new Map<string, unknown>();
const ucustakiler = new Map<string, Promise<unknown>>();

/** Saklanan son yanıt — yoksa `undefined`. İlk boyama için. */
export function onbellektenOku<T>(yol: string): T | undefined {
  return okumaOnbellegi.get(yol) as T | undefined;
}

/** Tek adresi düşürür (`yenile()` bunu kullanır). */
export function onbellektenDus(yol: string): void {
  okumaOnbellegi.delete(yol);
}

/** Hepsini düşürür: yazma sonrası ve oturum değişiminde. */
export function onbellegiTemizle(): void {
  okumaOnbellegi.clear();
  ucustakiler.clear();
}

function oku<T>(yol: string): Promise<T> {
  const ucusta = ucustakiler.get(yol);
  if (ucusta) return ucusta as Promise<T>;
  const soz = istek<T>(yol).then(
    (v) => {
      if (ucustakiler.get(yol) === soz) ucustakiler.delete(yol);
      okumaOnbellegi.set(yol, v);
      return v;
    },
    (e: unknown) => {
      if (ucustakiler.get(yol) === soz) ucustakiler.delete(yol);
      throw e;
    },
  );
  ucustakiler.set(yol, soz);
  return soz;
}

/** Yazma: sonucu döndürmeden önce saklanan her şeyi düşürür. */
async function yaz<T>(soz: Promise<T>): Promise<T> {
  try { return await soz; } finally { onbellegiTemizle(); }
}

export const api = {
  al: <T>(yol: string) => oku<T>(yol),
  gonder: <T>(yol: string, govde?: unknown) =>
    yaz(istek<T>(yol, { method: 'POST', body: JSON.stringify(govde ?? {}) })),
  guncelle: <T>(yol: string, govde?: unknown) =>
    yaz(istek<T>(yol, { method: 'PUT', body: JSON.stringify(govde ?? {}) })),
  sil: <T>(yol: string) => yaz(istek<T>(yol, { method: 'DELETE' })),
  dosya: <T>(yol: string, form: FormData) =>
    yaz(istek<T>(yol, { method: 'POST', body: form })),
};

/** Sorgu dizesini boş alanları atarak kurar. */
export function sorgu(
  parametreler: Readonly<Record<string, string | number | undefined | null>>,
): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(parametreler)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}
